import React from 'react';
import { motion } from 'framer-motion';
import {
  UserPlusIcon,
  LinkIcon,
  ChartBarIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

const HowItWorksSection: React.FC = () => {
  const steps = [
    {
      step: 1,
      icon: UserPlusIcon,
      title: 'أنشئ حساب منشأتك',
      description: 'سجل بيانات شركتك الأساسية في دقائق معدودة',
      color: 'from-blue-500 to-cyan-500',
      details: [
        'معلومات الشركة والسجل التجاري',
        'بيانات الاتصال والتحقق',
        'إعداد كلمة مرور آمنة'
      ]
    },
    {
      step: 2,
      icon: LinkIcon,
      title: 'اربط حساباتك البنكية ومنصة مدد',
      description: 'ربط آمن ومشفر مع جميع مصادر البيانات المالية',
      color: 'from-green-500 to-emerald-500',
      details: [
        'ربط البنوك عبر Open Banking',
        'تكامل مع منصة مدد للرواتب',
        'استيراد بيانات الفواتير'
      ]
    },
    {
      step: 3,
      icon: ChartBarIcon,
      title: 'ابدأ بتلقّي التحليلات والتوصيات',
      description: 'الذكاء الاصطناعي يحلل بياناتك ويقدم رؤى فورية',
      color: 'from-purple-500 to-pink-500',
      details: [
        'بطاقات ذكية لكل قسم',
        'تحليلات الأداء المالي',
        'توصيات تحسين الإنفاق'
      ]
    },
    {
      step: 4,
      icon: RocketLaunchIcon,
      title: 'فعّل الشراكات والتمويل الذكي',
      description: 'استفد من الخصومات التلقائية والتمويل الفوري',
      color: 'from-orange-500 to-red-500',
      details: [
        'خصومات تلقائية مع الشركاء',
        'تقييم ائتماني فوري',
        'طلب تمويل في 6 ساعات'
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="how-it-works" className="py-20 bg-white dark:bg-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            كيف تعمل المنصة؟
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            أربع خطوات بسيطة لتحويل إدارة أموال شركتك إلى نظام ذكي ومتطور
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="relative"
        >
          {/* Connection Lines */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-200 via-green-200 via-purple-200 to-orange-200 dark:from-blue-800 dark:via-green-800 dark:via-purple-800 dark:to-orange-800 transform -translate-y-1/2 z-0"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
            {steps.map((step, index) => (
              <motion.div
                key={step.step}
                variants={itemVariants}
                whileHover={{ scale: 1.05 }}
                className="relative"
              >
                <div className="glass-card p-8 text-center h-full hover:shadow-2xl transition-all duration-300 border border-gray-200/50 dark:border-gray-700/50">
                  {/* Step Number */}
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-r ${step.color} flex items-center justify-center text-white font-bold text-sm shadow-lg`}>
                      {step.step}
                    </div>
                  </div>

                  {/* Icon */}
                  <div className="mb-6 pt-4">
                    <div className={`w-16 h-16 mx-auto rounded-2xl bg-gradient-to-r ${step.color} p-4 shadow-lg`}>
                      <step.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                    {step.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                    {step.description}
                  </p>

                  {/* Details */}
                  <div className="space-y-2">
                    {step.details.map((detail, detailIndex) => (
                      <div
                        key={detailIndex}
                        className="flex items-center text-sm text-gray-500 dark:text-gray-400"
                      >
                        <div className={`w-1.5 h-1.5 rounded-full bg-gradient-to-r ${step.color} ml-2 rtl:ml-0 rtl:mr-2 flex-shrink-0`}></div>
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>

                  {/* Hover Effect */}
                  <div className={`absolute inset-0 rounded-xl bg-gradient-to-r ${step.color} opacity-0 hover:opacity-5 transition-opacity duration-300`}></div>
                </div>

                {/* Mobile Connection Line */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-center mt-6 mb-2">
                    <div className="w-0.5 h-8 bg-gradient-to-b from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700"></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <div className="glass-card p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              جاهز للبدء؟
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              انضم إلى مئات الشركات التي تستخدم FLYN لتحسين إدارتها المالية
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-primary text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
              >
                ابدأ مجاناً الآن
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white dark:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-xl font-semibold text-lg hover:shadow-lg transition-all duration-300"
              >
                احجز عرض تجريبي
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
